/**
 * FrontendTextPositionCalculator - Immediate caret positioning using frontend text state
 * 
 * Provides instant caret positioning that matches PaperCanvas text rendering:
 * - Uses same font metrics as PaperCanvas
 * - Calculates positions immediately without backend delay
 * - Provides smooth fallback until backend layout arrives
 */

import { CaretPosition } from '../interfaces/CaretInterfaces';
import { CoordinateSpace } from '../core/UnifiedCoordinateSystem';

export interface FontMetrics {
  fontFamily: string;
  fontSize: number;
  lineHeight: number;
  fontWeight: string;
  fontStyle: string;
}

export interface DocumentMetrics {
  margins: {
    top: number;
    left: number;
    right: number;
    bottom: number;
  };
  pageWidth: number;
  pageHeight: number;
}

export class FrontendTextPositionCalculator {
  private canvas: HTMLCanvasElement;
  private context: CanvasRenderingContext2D;
  
  constructor() {
    // Create offscreen canvas for text measurement
    this.canvas = document.createElement('canvas');
    this.context = this.canvas.getContext('2d')!;
  }
  
  /**
   * Calculate caret position using frontend text state (immediate)
   */
  calculatePosition(
    text: string, 
    charIndex: number, 
    fontMetrics: FontMetrics,
    documentMetrics: DocumentMetrics
  ): CaretPosition | null {
    
    if (charIndex < 0 || charIndex > text.length) {
      return null;
    }
    
    // Set up font for measurement
    this.context.font = `${fontMetrics.fontStyle} ${fontMetrics.fontWeight} ${fontMetrics.fontSize}px ${fontMetrics.fontFamily}`;
    
    // Handle empty document
    if (text.length === 0) {
      return this.getEmptyDocumentPosition(charIndex, documentMetrics);
    }
    
    // Handle end-of-text position
    if (charIndex === text.length) {
      return this.getEndOfTextPosition(text, fontMetrics, documentMetrics);
    }
    
    // Calculate position for character at charIndex
    return this.getCharacterPosition(text, charIndex, fontMetrics, documentMetrics);
  }
  
  /**
   * Get position for empty document
   */
  private getEmptyDocumentPosition(charIndex: number, documentMetrics: DocumentMetrics): CaretPosition {
    return {
      index: charIndex,
      coordinates: {
        x: documentMetrics.margins.left,
        y: documentMetrics.margins.top,
        space: CoordinateSpace.CANVAS,
        precision: 'pixel',
        timestamp: performance.now()
      },
      lineIndex: 0,
      runIndex: 0,
      charInRun: 0,
      context: {
        isWordStart: true,
        isLineStart: true,
        isLineEnd: false,
        isParagraphStart: true,
        isParagraphEnd: false,
        isInCitation: false,
        isInClause: false,
        isInHeader: false,
        isInFooter: false,
        isRTL: false
      },
      metadata: {
        method: 'fallback',
        confidence: 0.8,
        timing: { totalTime: 0 },
        debugInfo: { fallbackReason: 'empty_document_frontend' }
      }
    };
  }
  
  /**
   * Get position at end of text
   */
  private getEndOfTextPosition(
    text: string, 
    fontMetrics: FontMetrics, 
    documentMetrics: DocumentMetrics
  ): CaretPosition {
    
    const lines = text.split('\n');
    const lastLine = lines[lines.length - 1];
    
    // Measure width of last line
    const lastLineWidth = this.context.measureText(lastLine).width;
    
    return {
      index: text.length,
      coordinates: {
        x: documentMetrics.margins.left + lastLineWidth,
        y: documentMetrics.margins.top + (lines.length - 1) * fontMetrics.lineHeight,
        space: CoordinateSpace.CANVAS,
        precision: 'pixel',
        timestamp: performance.now()
      },
      lineIndex: lines.length - 1,
      runIndex: 0,
      charInRun: lastLine.length,
      context: {
        isWordStart: false,
        isLineStart: false,
        isLineEnd: true,
        isParagraphStart: false,
        isParagraphEnd: true,
        isInCitation: false,
        isInClause: false,
        isInHeader: false,
        isInFooter: false,
        isRTL: false
      },
      metadata: {
        method: 'fallback',
        confidence: 0.8,
        timing: { totalTime: 0 },
        debugInfo: { fallbackReason: 'end_of_text_frontend' }
      }
    };
  }
  
  /**
   * Get position for character at specific index
   */
  private getCharacterPosition(
    text: string, 
    charIndex: number, 
    fontMetrics: FontMetrics, 
    documentMetrics: DocumentMetrics
  ): CaretPosition {
    
    const lines = text.split('\n');
    let currentIndex = 0;
    
    // Find which line contains the character
    for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
      const line = lines[lineIndex];
      const lineEndIndex = currentIndex + line.length;
      
      if (charIndex >= currentIndex && charIndex <= lineEndIndex) {
        // Character is in this line
        const charInLine = charIndex - currentIndex;
        
        // Measure text up to character position
        const textBeforeChar = line.substring(0, charInLine);
        const textWidth = this.context.measureText(textBeforeChar).width;
        
        return {
          index: charIndex,
          coordinates: {
            x: documentMetrics.margins.left + textWidth,
            y: documentMetrics.margins.top + lineIndex * fontMetrics.lineHeight,
            space: CoordinateSpace.CANVAS,
            precision: 'pixel',
            timestamp: performance.now()
          },
          lineIndex,
          runIndex: 0,
          charInRun: charInLine,
          context: {
            isWordStart: charInLine === 0,
            isLineStart: charInLine === 0,
            isLineEnd: charInLine === line.length,
            isParagraphStart: lineIndex === 0,
            isParagraphEnd: lineIndex === lines.length - 1,
            isInCitation: false,
            isInClause: false,
            isInHeader: false,
            isInFooter: false,
            isRTL: false
          },
          metadata: {
            method: 'fallback',
            confidence: 0.8,
            timing: { totalTime: 0 },
            debugInfo: { fallbackReason: 'character_position_frontend' }
          }
        };
      }
      
      currentIndex = lineEndIndex + 1; // +1 for newline character
    }
    
    // Fallback (shouldn't reach here)
    return this.getEndOfTextPosition(text, fontMetrics, documentMetrics);
  }
  
  /**
   * Measure text width using current font
   */
  measureTextWidth(text: string, fontMetrics: FontMetrics): number {
    this.context.font = `${fontMetrics.fontStyle} ${fontMetrics.fontWeight} ${fontMetrics.fontSize}px ${fontMetrics.fontFamily}`;
    return this.context.measureText(text).width;
  }
  
  /**
   * Get character width for a single character
   */
  getCharacterWidth(char: string, fontMetrics: FontMetrics): number {
    return this.measureTextWidth(char, fontMetrics);
  }
}
